import { ApolloClient } from '@apollo/client';
import { Dispatch } from 'redux';
import { get, has } from 'lodash';

import { CallActionPayload, MethodResponse, UIStateCallback, ContextStore } from '../../types';
import { ContextAPI } from '../ImpactAiContextProvider';

interface WaitForContextPayload {
  contextPaths: string[];
  checkAllPaths?: boolean;
  timeout?: number;
  pollingInterval?: number;
}

export const waitForContext = async (
  payload: CallActionPayload,
  client: ApolloClient<unknown>,
  contextApi: ContextAPI,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dispatch: Dispatch<any>,
  uiStateCallback?: UIStateCallback
): Promise<MethodResponse> => {
  const {
    contextPaths,
    checkAllPaths = true,
    timeout = 30000,
  } = payload as unknown as WaitForContextPayload;

  if (!contextPaths || !Array.isArray(contextPaths) || contextPaths.length === 0) {
    throw new Error('contextPaths array is required for wait-for-context method');
  }

  if (uiStateCallback) {
    uiStateCallback('processing');
  }

  return new Promise<MethodResponse>((resolve, reject) => {
    const startTime = Date.now();
    let isResolved = false;
    let checkCount = 0;
    let unsubscribe: (() => void) | null = null;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const cleanup = () => {
      isResolved = true;
      if (unsubscribe) {
        unsubscribe();
        unsubscribe = null;
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    const checkContextValues = (currentContext: ContextStore) => {
      if (isResolved) {
        return;
      }

      checkCount += 1;

      const results = contextPaths.map((path) => {
        const value = get(currentContext, path);
        const exists = has(currentContext, path);
        const isPopulated = exists && value !== null && value !== undefined && value !== '';

        return {
          path,
          exists,
          isPopulated,
          value,
        };
      });

      const populatedPaths = results.filter((result) => result.isPopulated);
      const allPopulated = populatedPaths.length === contextPaths.length;
      const anyPopulated = populatedPaths.length > 0;
      const conditionMet = checkAllPaths ? allPopulated : anyPopulated;

      if (conditionMet) {
        cleanup();

        if (uiStateCallback) {
          uiStateCallback('complete');
        }

        const response = {
          message: `Successfully waited for context paths. Found: ${populatedPaths
            .map((r) => r.path)
            .join(', ')}`,
          waitResults: {
            contextPaths,
            checkAllPaths,
            results,
            conditionMet: true,
            waitTime: Date.now() - startTime,
            checkCount,
            method: 'subscription',
          },
        };

        resolve(response);
      }
    };

    timeoutId = setTimeout(() => {
      if (!isResolved) {
        cleanup();

        const timeoutError = new Error(
          `Timeout waiting for context paths: ${contextPaths.join(', ')} after ${checkCount} checks`
        );

        if (uiStateCallback) {
          uiStateCallback('error');
        }

        reject(timeoutError);
      }
    }, timeout);

    unsubscribe = contextApi.subscribe(checkContextValues);
    checkContextValues(contextApi.getFullContext());
  });
};
